/**
 * The frontend permission mirror (component-inventory.md:205 — `lib/permissions/`).
 *
 * It re-exports the GENERATED `@argus/permissions-catalogue` (ENG-10: mirrored from
 * the backend ROLE_PERMISSIONS via codegen, drift-gated in CI) and adds the React
 * primitives UI code uses to gate affordances against the CURRENT user's role.
 *
 * CC-2: components ask for a permission through these helpers — they never hard-code
 * a permission string in a conditional; the string lives in one place (the catalogue)
 * and the drift gate keeps it honest.
 */
import type { JSX, ReactNode } from 'react';

import { roleCan, type Permission, type Role } from '@argus/permissions-catalogue';

import { useAuth } from '../../store/auth-store.ts';

export { roleCan };
export type { Permission, Role };

/** Reactive: does the signed-in user's role grant `permission`? False when signed out. */
export function usePermission(permission: Permission): boolean {
  const role = useAuth((s) => s.user?.role);
  return role !== undefined ? roleCan(role, permission) : false;
}

/**
 * Declarative gate: renders `children` only when the current user holds `permission`,
 * else `fallback` (default: nothing). The permission is the single source — no inline
 * role/string checks in the calling component.
 */
export function Can({
  permission,
  children,
  fallback = null,
}: {
  permission: Permission;
  children: ReactNode;
  fallback?: ReactNode;
}): JSX.Element {
  const allowed = usePermission(permission);
  return <>{allowed ? children : fallback}</>;
}
